import type { BiomeId, LightId } from '../world/biome';
import { TerrainType } from '../world/terrain';
import { TERRAIN_PALETTE, type TerrainPalette } from './palette';

/**
 * Biome art (D132): ground colours, foliage, rock and edge colours per biome,
 * and the colour grade per time of day. Original palettes, painted-isometric
 * in spirit — soft, readable, a little desaturated.
 */
export interface BiomeArt {
  readonly terrain: Record<TerrainType, TerrainPalette>;
  readonly pine: readonly [string, string];
  readonly oak: readonly [string, string, string, string, string, string];
  readonly bush: readonly [string, string, string];
  readonly trunk: string;
  readonly snow: boolean;
  /** Pines drawn as saguaro-like cacti (desert). */
  readonly cactus: boolean;
  readonly reeds: string;
  readonly boulder: readonly [string, string, string];
  readonly flowers: readonly string[];
  readonly skirtTop: string;
  readonly skirtBottom: string;
  /** Water edge highlight (foam / glow). */
  readonly shore: string;
}

const T = (base: string, light: string, dark: string, accent: string): TerrainPalette => ({ base, light, dark, accent });

const VALLEY: BiomeArt = {
  terrain: TERRAIN_PALETTE,
  pine: ['#2f5a2c', '#3f7537'],
  oak: ['#3d6a2c', '#365f27', '#44762f', '#3a6529', '#335a24', '#4f8536'],
  bush: ['#3c6a2b', '#355f26', '#4a7d33'],
  trunk: '#5a3d22',
  snow: false,
  cactus: false,
  reeds: '#6f8a3a',
  boulder: ['#7d7d76', '#9d9d94', '#5f5f59'],
  flowers: ['#f1d14a', '#efe9e0', '#d9708a', '#b48ee0'],
  skirtTop: '#4e4236',
  skirtBottom: '#26201b',
  shore: '#9ec4b6',
};

export const BIOME_ART: Record<BiomeId, BiomeArt> = {
  valley: VALLEY,
  prairie: {
    terrain: {
      [TerrainType.Grass]: T('#b0972f', '#c4ab44', '#8d7822', '#d6bf5a'),
      [TerrainType.Meadow]: T('#bfa23c', '#d2b552', '#9a8228', '#e2c868'),
      [TerrainType.Dirt]: T('#a7844c', '#b9965c', '#87683a', '#6e5430'),
      [TerrainType.Forest]: T('#6f6a28', '#827c34', '#59551e', '#8f8a3e'),
      [TerrainType.Rock]: T('#8c7b62', '#a69478', '#6c5c48', '#55483a'),
      [TerrainType.Water]: T('#3d8f8c', '#5bb3ad', '#2a6a68', '#a6e2da'),
      [TerrainType.Road]: T('#b49a6a', '#c5ab7c', '#957e54', '#7e6a46'),
      [TerrainType.Ruins]: T('#857a66', '#9a8f7a', '#6b6252', '#4e473c'),
    },
    pine: ['#3d5222', '#51692c'],
    oak: ['#56692a', '#4b5d24', '#617630', '#7a3e22', '#4a5a22', '#6d8236'],
    bush: ['#6b6a2c', '#5a5a24', '#807c36'],
    trunk: '#5e4226',
    snow: false,
    cactus: false,
    reeds: '#8a8a3a',
    boulder: ['#a49072', '#c0ab8a', '#7d6c54'],
    flowers: ['#e8d06a', '#f2e8c8', '#d7863a'],
    skirtTop: '#6a5434',
    skirtBottom: '#33281a',
    shore: '#e6d9a6',
  },
  desert: {
    terrain: {
      [TerrainType.Grass]: T('#d4b27a', '#e3c58e', '#b8955e', '#c7a16a'),
      [TerrainType.Meadow]: T('#c9a45e', '#d8b672', '#a9864a', '#b89150'),
      [TerrainType.Dirt]: T('#bf8f58', '#d1a16a', '#9c7044', '#87603a'),
      [TerrainType.Forest]: T('#a68752', '#b89862', '#8a6e42', '#7a7038'),
      [TerrainType.Rock]: T('#a5633e', '#c07c52', '#7c4528', '#5a3220'),
      [TerrainType.Water]: T('#3f9293', '#62b8b5', '#2b6d6e', '#b4ebe2'),
      [TerrainType.Road]: T('#9c8b70', '#ae9d82', '#7e6f58', '#665a48'),
      [TerrainType.Ruins]: T('#a08c70', '#b5a184', '#7f6e56', '#5e5242'),
    },
    pine: ['#5d7a3a', '#6f8f46'],
    oak: ['#6b6a35', '#5c5c2c', '#7a783d', '#6a5a2c', '#58552a', '#848244'],
    bush: ['#7a7038', '#6a6230', '#8c8242'],
    trunk: '#6a4a2a',
    snow: false,
    cactus: true,
    reeds: '#8a8a40',
    boulder: ['#b06e48', '#cc8a60', '#86502f'],
    flowers: ['#e0b050', '#d07040'],
    skirtTop: '#8a6640',
    skirtBottom: '#3d2a18',
    shore: '#f0deb0',
  },
  swamp: {
    terrain: {
      [TerrainType.Grass]: T('#3b4b3d', '#4b5d4a', '#2c392e', '#5e704e'),
      [TerrainType.Meadow]: T('#4a5935', '#5a6a40', '#38452a', '#6c7c48'),
      [TerrainType.Dirt]: T('#4a4a3e', '#5a5a4c', '#38382f', '#2c2c25'),
      [TerrainType.Forest]: T('#2c3a2b', '#384a36', '#212c20', '#46583e'),
      [TerrainType.Rock]: T('#4c5453', '#5f6867', '#3a4140', '#2c3231'),
      [TerrainType.Water]: T('#2ba790', '#56d0b6', '#1c7766', '#a8f4e0'),
      [TerrainType.Road]: T('#4a4a44', '#5a5a52', '#3a3a35', '#2c2c28'),
      [TerrainType.Ruins]: T('#4f544e', '#626860', '#3c403b', '#2c2f2b'),
    },
    pine: ['#1f3428', '#2b4733'],
    oak: ['#2e4a33', '#27402c', '#35553a', '#2a4430', '#223a28', '#3d5f42'],
    bush: ['#2f4a34', '#27402c', '#3a583d'],
    trunk: '#3a2e24',
    snow: false,
    cactus: false,
    reeds: '#5e7a3a',
    boulder: ['#56605d', '#6c7774', '#3f4744'],
    flowers: ['#8fd6a0', '#c2e6a0'],
    skirtTop: '#2e3a2e',
    skirtBottom: '#141a14',
    shore: '#7ff0d0',
  },
  tundra: {
    terrain: {
      [TerrainType.Grass]: T('#e3ebf0', '#f4f8fb', '#c3d0d8', '#ffffff'),
      [TerrainType.Meadow]: T('#d6e1e7', '#e8f0f4', '#b6c5cf', '#f6fafc'),
      [TerrainType.Dirt]: T('#9a948e', '#aca69f', '#7c7671', '#625d58'),
      [TerrainType.Forest]: T('#c9d6de', '#dbe5eb', '#a8b8c3', '#eef3f6'),
      [TerrainType.Rock]: T('#8c96a0', '#b2bcc5', '#68727c', '#4e565e'),
      [TerrainType.Water]: T('#1e3d56', '#2e5878', '#132b3f', '#86afca'),
      [TerrainType.Road]: T('#7e7b77', '#908d88', '#64615d', '#4c4a47'),
      [TerrainType.Ruins]: T('#9ba1a7', '#b2b8be', '#7c8288', '#5c6166'),
    },
    pine: ['#1e3a2d', '#2a4c3a'],
    oak: ['#3a4a3e', '#334236', '#435446', '#2e3c32', '#2a372d', '#4c5e4f'],
    bush: ['#4a5a4e', '#3e4c42', '#56685a'],
    trunk: '#4a3a2c',
    snow: true,
    cactus: false,
    reeds: '#8a9a8c',
    boulder: ['#98a2ab', '#c4ccd3', '#6f7881'],
    flowers: ['#ffffff', '#e6eef4'],
    skirtTop: '#6a747c',
    skirtBottom: '#2a3036',
    shore: '#e8f4fb',
  },
  autumn: {
    terrain: {
      [TerrainType.Grass]: T('#8b6b2d', '#a17e36', '#6c5221', '#c08a38'),
      [TerrainType.Meadow]: T('#ad6a2b', '#c27c34', '#8a5220', '#d99440'),
      [TerrainType.Dirt]: T('#6a4b2b', '#7c5a36', '#523a21', '#3e2c19'),
      [TerrainType.Forest]: T('#7a5a28', '#8e6a30', '#5e451f', '#a07a38'),
      [TerrainType.Rock]: T('#c2b07a', '#d8c78e', '#9a8a5a', '#7a6c45'),
      [TerrainType.Water]: T('#2b6b5f', '#3f8a7c', '#1d4d44', '#86c4b4'),
      [TerrainType.Road]: T('#5b4733', '#6c563f', '#463626', '#33281c'),
      [TerrainType.Ruins]: T('#7a6a52', '#8e7c62', '#5e523f', '#433a2d'),
    },
    pine: ['#4a5a28', '#5c6e30'],
    oak: ['#c8641e', '#b0521a', '#d9822a', '#a8441a', '#9a3e16', '#e8a03a'],
    bush: ['#b8601e', '#9a4a18', '#d07a28'],
    trunk: '#4e3420',
    snow: false,
    cactus: false,
    reeds: '#9a7a3a',
    boulder: ['#c8b680', '#e0d09a', '#9c8c5c'],
    flowers: ['#e8a03a', '#d0602a', '#f0d070'],
    skirtTop: '#5e4228',
    skirtBottom: '#2a1c10',
    shore: '#d9c88e',
  },
  canyon: {
    terrain: {
      [TerrainType.Grass]: T('#5b402c', '#6f4f37', '#46311f', '#7c5a3c'),
      [TerrainType.Meadow]: T('#7a6a2e', '#8c7c38', '#5f5222', '#a0903e'),
      [TerrainType.Dirt]: T('#4b3525', '#5c4430', '#39281b', '#2a1d13'),
      [TerrainType.Forest]: T('#3f4b25', '#4c5a2e', '#2f381b', '#5a6a34'),
      [TerrainType.Rock]: T('#8b5b45', '#a8715b', '#6b4333', '#4e3025'),
      [TerrainType.Water]: T('#2a6b63', '#3c8a80', '#1c4c46', '#86c6ba'),
      [TerrainType.Road]: T('#5b4b3b', '#6c5a48', '#46392c', '#32291f'),
      [TerrainType.Ruins]: T('#6b5b4b', '#7e6c5a', '#524538', '#3a3128'),
    },
    pine: ['#34482a', '#425a34'],
    oak: ['#4a5a2a', '#405024', '#566a30', '#3a4a22', '#34421e', '#627636'],
    bush: ['#5a5a2a', '#4a4a24', '#6a6a32'],
    trunk: '#4a3020',
    snow: false,
    cactus: false,
    reeds: '#6a7a3a',
    boulder: ['#9a6a52', '#b8846a', '#74493a'],
    flowers: ['#d0a040', '#c06a3a'],
    skirtTop: '#5a3a28',
    skirtBottom: '#24160e',
    shore: '#b8d0b0',
  },
  deadwood: {
    terrain: {
      [TerrainType.Grass]: T('#2f3b31', '#3d4b3e', '#232d25', '#4b5a43'),
      [TerrainType.Meadow]: T('#3b4731', '#4a583c', '#2d3725', '#566648'),
      [TerrainType.Dirt]: T('#3b3b33', '#4a4a40', '#2d2d27', '#22221d'),
      [TerrainType.Forest]: T('#25311f', '#303e29', '#1b2417', '#3c4c33'),
      [TerrainType.Rock]: T('#4b5151', '#5e6565', '#393e3e', '#2a2e2e'),
      [TerrainType.Water]: T('#245b55', '#357a72', '#173e3a', '#6cb0a4'),
      [TerrainType.Road]: T('#3d3d37', '#4a4a43', '#2f2f2a', '#232320'),
      [TerrainType.Ruins]: T('#4a4c48', '#5a5c57', '#393b37', '#2a2c29'),
    },
    pine: ['#18281d', '#223828'],
    oak: ['#263a28', '#203222', '#2c442e', '#1c2c1e', '#18261a', '#344e36'],
    bush: ['#2a3a2a', '#223022', '#324632'],
    trunk: '#2e241c',
    snow: false,
    cactus: false,
    reeds: '#4e623a',
    boulder: ['#555c5c', '#6c7474', '#3e4444'],
    flowers: ['#8a9a7a'],
    skirtTop: '#2a2e28',
    skirtBottom: '#121410',
    shore: '#6cc8b8',
  },
};

export const biomeArt = (b: BiomeId | undefined): BiomeArt => BIOME_ART[b ?? 'valley'];

/** Colour grade per time of day: a multiply tint over the world, plus how strong building lights are. */
export const LIGHT_GRADE: Record<LightId, { tint: number; lights: number }> = {
  day: { tint: 0xffffff, lights: 0 },
  dusk: { tint: 0xd8a888, lights: 0.55 },
  night: { tint: 0x56707e, lights: 1 },
};

/** A biome palette re-based on the painted textures' real colours, so edge blends and the minimap match the paint. */
export function paintedArt(base: BiomeArt, colours: Partial<Record<TerrainType, string>>): BiomeArt {
  const terrain = { ...base.terrain };
  const mix = (hex: string, k: number): string => {
    const n = parseInt(hex.slice(1), 16);
    const f = (v: number): string => Math.round(Math.max(0, Math.min(255, v * k))).toString(16).padStart(2, '0');
    return `#${f(n >> 16)}${f((n >> 8) & 255)}${f(n & 255)}`;
  };
  for (const [t, c] of Object.entries(colours) as [string, string | undefined][]) {
    if (!c) continue;
    const id = Number(t) as TerrainType;
    terrain[id] = { base: c, light: mix(c, 1.12), dark: mix(c, 0.82), accent: terrain[id].accent };
  }
  return { ...base, terrain };
}
